import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ArrowRight, Check, Globe, User, Calendar, Mail, Sparkles, ChevronDown, Lock, Eye, EyeOff, Loader2, FileText, MapPin, Briefcase, Search } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { parseN8NResponse } from '../../utils/parseN8NResponse';
import { identifyUser, track } from '../../lib/analytics';

// Initial static list to avoid empty state, will be merged/replaced by DB data
const INITIAL_COUNTRIES = [
    { code: 'DE', name: 'Allemagne', flag: '🇩🇪', localFlag: '/assets/flags/no_background/DE.png' },
    { code: 'BE', name: 'Belgique', flag: '🇧🇪', localFlag: '/assets/flags/no_background/BE.png' },
    { code: 'ES', name: 'Espagne', flag: '🇪🇸', localFlag: '/assets/flags/no_background/ES.png' },
    { code: 'IT', name: 'Italie', flag: '🇮🇹', localFlag: '/assets/flags/no_background/IT.png' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹', localFlag: '/assets/flags/no_background/PT.png' },
    { code: 'CH', name: 'Suisse', flag: '🇨🇭', localFlag: '/assets/flags/no_background/CH.png' },
    { code: 'GB', name: 'Royaume-Uni', flag: '🇬🇧', localFlag: '/assets/flags/no_background/GB.png' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', localFlag: '/assets/flags/no_background/CA.png' },
    { code: 'US', name: 'États-Unis', flag: '🇺🇸', localFlag: '/assets/flags/no_background/US.png' },
    { code: 'AU', name: 'Australie', flag: '🇦🇺', localFlag: '/assets/flags/no_background/AU.png' },
    { code: 'JP', name: 'Japon', flag: '🇯🇵', localFlag: '/assets/flags/no_background/JP.png' },
    { code: 'CN', name: 'Chine', flag: '🇨🇳', localFlag: '/assets/flags/no_background/CN.png' },
    { code: 'KR', name: 'Corée du Sud', flag: '🇰🇷', localFlag: '/assets/flags/no_background/KR.png' },
    { code: 'SG', name: 'Singapour', flag: '🇸🇬', localFlag: '/assets/flags/no_background/SG.png' },
    { code: 'AE', name: 'Émirats Arabes Unis (Dubaï)', flag: '🇦🇪', localFlag: '/assets/flags/no_background/AE.png' },
    { code: 'MA', name: 'Maroc', flag: '🇲🇦', localFlag: '/assets/flags/no_background/MA.png' },
    { code: 'SN', name: 'Sénégal', flag: '🇸🇳', localFlag: '/assets/flags/no_background/SN.png' },
    { code: 'BR', name: 'Brésil', flag: '🇧🇷', localFlag: '/assets/flags/no_background/BR.png' },
    { code: 'MX', name: 'Mexique', flag: '🇲🇽', localFlag: '/assets/flags/no_background/MX.png' },
    { code: 'NZ', name: 'Nouvelle-Zélande', flag: '🇳🇿', localFlag: null },
    { code: 'OTHER', name: 'Autre', flag: '🌍', localFlag: '/assets/flags/no_background/OTHER.png' }
];

// Helper function for sector icons - matches actual parent_label_fr values
const getSectorIcon = (name) => {
    const lower = name.toLowerCase();
    // Exact matches for DB labels
    if (lower.includes('achats') || lower.includes('logistique') || lower.includes('transport')) return '🚚';
    if (lower.includes('agriculture') || lower.includes('agroalimentaire')) return '🌾';
    if (lower.includes('commerce')) return '🛒';
    if (lower.includes('construction')) return '🏗️';
    if (lower.includes('droit') || lower.includes('fiscal')) return '⚖️';
    if (lower.includes('finance') || lower.includes('comptabilit') || lower.includes('banque')) return '💰';
    if (lower.includes('gestion') && lower.includes('production')) return '⚙️';
    if (lower.includes('industrie') || lower.includes('ingénierie')) return '🏭';
    if (lower.includes('informatique') || lower.includes('télécom') || lower.includes('tech')) return '💻';
    if (lower.includes('marketing') || lower.includes('communication')) return '📣';
    if (lower.includes('ressources humaines') || lower.includes('rh')) return '👥';
    if (lower.includes('santé') || lower.includes('pharma')) return '🏥';
    if (lower.includes('tourisme') || lower.includes('hôtel')) return '✈️';
    if (lower.includes('aéro') || lower.includes('défense') || lower.includes('spatial')) return '🚀';
    if (lower.includes('auto') || lower.includes('mobilité')) return '🚗';
    if (lower.includes('énergie') || lower.includes('environnement')) return '⚡';
    if (lower.includes('conseil') || lower.includes('audit')) return '📊';
    if (lower.includes('luxe') || lower.includes('mode')) return '💎';
    if (lower.includes('média')) return '📱';
    if (lower.includes('académique') || lower.includes('enseignement') || lower.includes('recherche')) return '🎓';
    if (lower.includes('naturelle') || lower.includes('environnement') || lower.includes('biologie')) return '🌱';
    if (lower.includes('public') || lower.includes('état') || lower.includes('administration')) return '🏛️';
    if (lower.includes('services aux entreprises')) return '🤝';
    if (lower.includes('systèmes d\'information') || lower.includes('si')) return '🖥️';
    return '💼';
};

// Fallback static SECTORS for initial render
const SECTORS = [
    { id: 'informatique', name: 'Informatique & Tech', icon: '💻' },
    { id: 'finance', name: 'Banque & Finance', icon: '🏦' },
    { id: 'energie', name: 'Énergie', icon: '⚡' },
    { id: 'sante', name: 'Santé & Pharma', icon: '🏥' },
    { id: 'industrie', name: 'Industrie', icon: '🏭' },
    { id: 'commerce', name: 'Commerce', icon: '🛒' },
    { id: 'conseil', name: 'Conseil & Audit', icon: '📊' },
    { id: 'transport', name: 'Transport & Logistique', icon: '🚚' },
    { id: 'aero', name: 'Aéronautique & Défense', icon: '🚀' },
    { id: 'auto', name: 'Automobile', icon: '🚗' }
];

const MASCOTS = [
    "/assets/mascots/Man_1.png.png",
    "/assets/mascots/Woman_1.png.png",
    "/assets/mascots/Man_2_happy.png.png",
    "/assets/mascots/Woman_2.png.png",
    "/assets/mascots/Man_1_tech.png.png",
    "/assets/mascots/woman_6_labo.png.png",
    "/assets/mascots/Woman_3_brown.png", // Fixed extension
    "/assets/mascots/man-3.png",
    "/assets/mascots/woman-3.png",
    "/assets/mascots/man-4.png",
    "/assets/mascots/woman-4.png",
    "/assets/mascots/man-5.png",
    "/assets/mascots/woman-5.png"
];

const TightLayout = ({ children, title, illustration, subtitle, topElement, animateIllustration = true, largeIllustration = false }) => (
    <div className="w-full max-w-xl mx-auto flex flex-col items-center h-[520px] relative">
        {topElement && <div className="mb-6">{topElement}</div>}
        {illustration && (
            // Updated Dimensions and Margin for Step 1 (largeIllustration)
            <div className={`${largeIllustration ? 'w-[400px] h-[260px] mb-8' : 'w-40 h-32 -mb-2'} relative flex-shrink-0 transition-all duration-300`}>
                <motion.img
                    src={illustration}
                    alt="Illustration"
                    className="w-full h-full object-contain"
                    animate={animateIllustration ? {
                        y: [0, -10, 0],
                        rotate: [0, 1, -1, 0]
                    } : {}}
                    transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />
            </div>
        )}
        <h2 className="text-xl font-black text-slate-900 mb-4 tracking-tight text-center leading-tight flex-shrink-0">
            {title}
        </h2>
        <div className="w-full flex flex-col items-center flex-1 overflow-y-auto px-4 custom-scrollbar">
            {children}
        </div>
        {subtitle && <p className="mt-4 text-sm text-slate-400 font-medium text-center px-8 flex-shrink-0">{subtitle}</p>}
    </div>
);

const ProgressBar = ({ current, total }) => {
    const progress = (current / total) * 100;
    return (
        <div className="w-full max-w-xs mx-auto mb-2">
            <div className="flex justify-between text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">
                <span>Étape {current} sur {total}</span>
                <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="h-full bg-blue-600 rounded-full"
                />
            </div>
        </div>
    );
};

// --- Local Circular Progress Component (Ported from Profile) ---
const CircularProgress = ({ progress }) => {
    const [messageIndex, setMessageIndex] = useState(0);
    const LoadingMessages = [
        "Initialisation des neurones de l'IA...",
        "Décodage de votre ADN professionnel...",
        "Analyse de vos super-pouvoirs...",
        "Alignement avec les opportunités...",
        "Polissage de votre profil...",
        "Succès imminent..."
    ];

    useEffect(() => {
        const messageInterval = setInterval(() => {
            setMessageIndex(prev => (prev + 1) % LoadingMessages.length);
        }, 1500);
        return () => clearInterval(messageInterval);
    }, []);

    return (
        <div className="flex flex-col items-center justify-center p-8 animate-in fade-in duration-500">
            <div className="relative w-40 h-40 flex items-center justify-center">
                <svg className="absolute w-full h-full transform -rotate-90 overflow-visible">
                    <circle cx="80" cy="80" r="54" stroke="#dbeafe" strokeWidth="16" fill="transparent" />
                    <circle
                        cx="80" cy="80" r="54" stroke="#2563eb" strokeWidth="16" fill="transparent"
                        strokeDasharray={2 * Math.PI * 54}
                        strokeDashoffset={(2 * Math.PI * 54) - (progress / 100) * (2 * Math.PI * 54)}
                        strokeLinecap="round"
                        className="transition-[stroke-dashoffset] duration-200 ease-linear"
                        style={{ filter: `drop-shadow(0 0 ${6 + (progress / 20)}px rgba(37, 99, 235, ${0.4 + (progress / 300)}))` }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-blue-900">
                    <span className="text-3xl font-black tracking-tighter">{Math.round(progress)}%</span>
                </div>
            </div>
            <div className="h-8 mt-8 flex items-center justify-center">
                <AnimatePresence mode="wait">
                    <motion.p
                        key={messageIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="text-blue-600/80 font-bold text-sm text-center"
                    >
                        {LoadingMessages[messageIndex]}
                    </motion.p>
                </AnimatePresence>
            </div>
        </div>
    );
};

const OnboardingFlow = ({ onComplete }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [direction, setDirection] = useState(0);
    const [emailError, setEmailError] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isWaitingForVerification, setIsWaitingForVerification] = useState(false);
    const [showWelcome, setShowWelcome] = useState(false); // NEW STATE

    // Écoute la confirmation email en temps réel — avance automatiquement dès validation
    useEffect(() => {
        if (!isWaitingForVerification) return;
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user) {
                setStep(7);
            }
        });
        return () => subscription.unsubscribe();
    }, [isWaitingForVerification]);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        age: '',
        preferredCountries: [],
        preferredSectors: [],
        email: '',
        newsletter: false,
        tos: false
    });

    const [availableCountries, setAvailableCountries] = useState(INITIAL_COUNTRIES);
    const [availableSectors, setAvailableSectors] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [countrySearch, setCountrySearch] = useState('');
    const [sectorSearch, setSectorSearch] = useState('');

    useEffect(() => {
        const fetchFormData = async () => {
            setIsLoadingData(true);
            try {
                // Fetch Countries - use country_code for uniqueness
                const { data: countryData } = await supabase
                    .from('offres_vie_bf_public')
                    .select('country_code, pays')
                    .eq('url_live', 'active')
                    .not('country_code', 'is', null);

                if (countryData) {
                    // Create unique country list by country_code
                    const countryMap = new Map();

                    // 1. Start with INITIAL_COUNTRIES to ensure favourites like NZ are present
                    INITIAL_COUNTRIES.forEach(c => {
                        if (c.code !== 'OTHER') {
                            countryMap.set(c.code, c.name);
                        }
                    });

                    // 2. Add/Update from DB (if new or different name)
                    countryData.forEach(d => {
                        if (d.country_code && d.pays && d.pays !== 'France') {
                            countryMap.set(d.country_code, d.pays);
                        }
                    });

                    // 3. Convert back to array with flags
                    const allCountries = Array.from(countryMap.entries()).map(([code, name]) => {
                        // Check if we have this country in INITIAL_COUNTRIES for local flag
                        const initial = INITIAL_COUNTRIES.find(c => c.code === code);
                        return {
                            code: code,
                            name: name,
                            flag: initial?.flag || '🌍',
                            localFlag: initial?.localFlag || null
                        };
                    });

                    // Sort alphabetically by name
                    const sortedCountries = allCountries.sort((a, b) => a.name.localeCompare(b.name));

                    // Add 'Autre' at the end if not present (though initialized via map entry usually not needed if OTHER is special)
                    // Actually INITIAL_COUNTRIES has OTHER, but we filtered it out of map. Let's add it back manually if needed, or rely on INITIAL_COUNTRIES having it.
                    // The map key is code. 'OTHER' is code.
                    // Let's just append 'Autre' manually at the end if desired, or let it sort. 
                    // 'Autre' is usually last. 
                    // Let's filter 'OTHER' out of sorting and append it.
                    const finalCountries = sortedCountries.filter(c => c.code !== 'OTHER');
                    finalCountries.push(INITIAL_COUNTRIES.find(c => c.code === 'OTHER'));

                    setAvailableCountries(finalCountries);
                }

                // Fetch Sectors
                const { data: sectorData } = await supabase
                    .from('offres_vie_bf_public')
                    .select('parent_label_fr')
                    .eq('url_live', 'active')
                    .not('parent_label_fr', 'is', null);

                if (sectorData) {
                    const uniqueSectors = [...new Set(sectorData.map(d => d.parent_label_fr).filter(s => s && s.trim()))].sort();
                    const mappedSectors = uniqueSectors.map((name, index) => ({
                        id: name, // Use name as ID for persistence matching
                        name: name,
                        icon: getSectorIcon(name)
                    }));
                    setAvailableSectors(mappedSectors);
                }

            } catch (error) {
                console.error("Error fetching onboarding data:", error);
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchFormData();
    }, []);

    // PERSISTENCE: Sync data to profiles table when reaching Step 7 (Authenticated)
    useEffect(() => {
        if (step === 7) {
            const syncProfile = async () => {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.user) {
                    const updates = {
                        first_name: formData.firstName,
                        last_name: formData.lastName,
                        age: formData.age === '' ? null : formData.age,
                        preferred_countries: formData.preferredCountries,
                        preferred_sectors: formData.preferredSectors,
                        newsletter_optin: formData.newsletter,
                        is_premium: true
                    };

                    console.log("Saving onboarding data to profiles:", updates);

                    const { error } = await supabase
                        .from('profiles')
                        .update(updates)
                        .eq('id', session.user.id);

                    if (error) console.error("Error saving profile data:", error);
                }
            };
            syncProfile();
        }
    }, [step, formData]);


    const nextStep = async () => {
        if (step === 1 && (!formData.firstName || !formData.lastName)) return;
        if (step === 3 && (!formData.age || formData.age < 18)) return;
        if (step === 4 && formData.preferredCountries.length === 0) return;
        if (step === 5 && formData.preferredSectors.length === 0) return;
        if (step === 6) {
            if (!formData.email || !formData.email.includes('@')) {
                setEmailError('Email invalide');
                return;
            }
            // Email format check
            // Note: Existence check is handled by Supabase Auth response (requires User Enumeration Protection OFF for explicit error)
            setEmailError('');
        }

        // Final Step Logic is handled by authentication buttons, not "nextStep"
        if (step === 1) {
            setDirection(1);
            setStep(3); // Step 2 (avatar) supprimé — on passe directement à l'âge
        } else if (step < 8) {
            setDirection(1);
            setStep(s => s + 1);
        }
    };

    // NEW: CV State
    const [cvFile, setCvFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const N8N_WEBHOOK_URL = "https://maxbconnect.duckdns.org/webhook/4665c493-dbec-448c-b586-e8bfed71cd3b";

    // Image preloading for smoother carousel
    useEffect(() => {
        const imagesToPreload = [
            ...MASCOTS,
            "/assets/mascots.jpg",
            "/assets/paper_plane.png",
            "/assets/onboarding_globe.png"
        ];

        imagesToPreload.forEach(src => {
            const img = new Image();
            img.src = src;
        });

        // Check for existing session (e.g. Email Redirect or Reload)
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                // If user is already logged in, go to CV step
                setStep(7);
                // Try to fill formData from metadata if available
                if (session.user.user_metadata) {
                    setFormData(prev => ({
                        ...prev,
                        firstName: session.user.user_metadata.first_name || '',
                        lastName: session.user.user_metadata.last_name || '',
                        avatar: session.user.user_metadata.avatar_url || MASCOTS[0]
                    }));
                }
            }
        };
        checkSession();

        // LISTEN FOR AUTH CHANGES (e.g. Email Confirmed in another tab or same tab redirect)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN' && session) {
                setStep(7); // Auto-advance to CV upload
                // If this is a fresh sign-in (likely from email link), show welcome
                // To be safe, we show it. If it's a re-login, it's also nice.
                // We can check if profile is incomplete to separate re-login from new user, but simple is better.
                setShowWelcome(true);
            }
        });

        return () => subscription.unsubscribe();

    }, []);

    const steps = [
        { id: 1, title: 'Identité', icon: User },
        { id: 3, title: 'Âge', icon: Calendar },
        { id: 4, title: 'Pays', icon: MapPin },
        { id: 5, title: 'Secteurs', icon: Briefcase },
        { id: 6, title: 'Compte', icon: Mail },
        { id: 7, title: 'CV', icon: FileText },
    ];



    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (step === 6) {
                if (!isWaitingForVerification) handleEmailSignUp();
            } else {
                nextStep();
            }
        }
    };

    const prevStep = () => {
        if (step === 3) {
            setDirection(-1);
            setStep(1); // Skip back past step 2 (avatar supprimé)
        } else if (step > 1) {
            setDirection(-1);
            setStep(s => s - 1);
        }
    };

    const cleanName = (val) => {
        return val.replace(/[^a-zA-ZÀ-ÿ\s-]/g, '');
    };

    const getAvatarScale = (index) => {
        if (index === 10) return 1.76;
        if ([0, 1, 2, 4, 5].includes(index)) return 1.44;
        return 1;
    };

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 500 : -500,
            opacity: 0,
            scale: 0.98
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
            scale: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? 500 : -500,
            opacity: 0,
            scale: 0.98
        })
    };

    // Images paths
    const mascotsImg = "/assets/mascots.jpg";
    const paperPlaneImg = "/assets/paper_plane.png";
    const planetVideo = "/assets/planet_rotation.mp4";

    const [avatarIndex, setAvatarIndex] = useState(0);
    const [showAvatarPanel, setShowAvatarPanel] = useState(false);

    const nextAvatar = () => setAvatarIndex(prev => (prev + 1) % MASCOTS.length);
    const prevAvatar = () => setAvatarIndex(prev => (prev - 1 + MASCOTS.length) % MASCOTS.length);

    useEffect(() => {
        setFormData(prev => ({ ...prev, avatar: MASCOTS[avatarIndex] }));
    }, [avatarIndex]);


    // --- AUTH ACTIONS ---

    const handleCvUpload = async () => {
        if (!cvFile) {
            onComplete(formData); // Skip if no file
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        // Simulate progress for UX
        const progressInterval = setInterval(() => {
            setUploadProgress(prev => Math.min(prev + 1, 95));
        }, 100);

        try {
            // Robust Session Fetching with Retry
            let session = null;
            for (let i = 0; i < 3; i++) {
                const { data } = await supabase.auth.getSession();
                if (data.session) {
                    session = data.session;
                    break;
                }
                await new Promise(r => setTimeout(r, 500)); // Wait 500ms
            }

            if (!session) throw new Error("Session introuvable. Si vous venez de créer votre compte, vérifiez que vous avez bien cliqué sur le lien de confirmation reçu par email.");
            const user = session.user;

            const fileExt = cvFile.name.split('.').pop();
            const filePath = `${user.id}/${Date.now()}_cv.${fileExt}`;

            // 1. Upload to Supabase
            const { error: uploadError } = await supabase.storage
                .from('resumes')
                .upload(filePath, cvFile);

            if (uploadError) throw uploadError;

            // 2. Get Signed URL for n8n
            const { data: signedData, error: signError } = await supabase.storage
                .from('resumes')
                .createSignedUrl(filePath, 3600); // Valid 1 hour

            if (signError) throw signError;

            const signedUrl = signedData.signedUrl;

            // 3. Trigger n8n Webhook AND Get Data
            let cvData = null;
            try {
                console.log("Triggering analysis...");
                const response = await fetch(N8N_WEBHOOK_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: user.id,
                        fileUrl: signedUrl
                    })
                });

                if (response.ok) {
                    const jsonData = await response.json();
                    console.log("Analysis received (Raw):", jsonData);
                    // Use normalized parser
                    cvData = parseN8NResponse(jsonData);
                    console.log("Analysis parsed (Normalized):", cvData);
                    track('cv_uploaded', { context: 'onboarding' });
                } else {
                    console.warn("Webhook returned status:", response.status);
                }
            } catch (err) {
                console.error("Webhook analysis failed:", err);
                // Continue without data
            }

            // 4. Update Profile with CV URL (internal reference) AND Data
            const updatePayload = {
                cv_url: filePath
            };
            if (cvData) {
                updatePayload.cv_data = cvData; // storing normalized data now
            }

            await supabase.from('profiles').update(updatePayload).eq('id', user.id);

            // Finish Progress
            clearInterval(progressInterval);
            setUploadProgress(100);

            // Wait a moment for the 100% to be seen
            setTimeout(() => {
                // Redirect to Profile for validation
                navigate('/profil', { state: { cvData } });
            }, 800);

        } catch (error) {
            clearInterval(progressInterval);
            console.error('Error uploading CV:', error);
            alert("Erreur lors de l'envoi du CV : " + error.message);
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setCvFile(e.target.files[0]);
        }
    };

    // ... (Wait, I'm just replacing the specific function logic, but I need to parse first.
    // I'll skip ReplaceFileContent this turn and do ViewFile first). existing Auth wrappers ...
    // Note: handleEmailSignUp should now setStep(7) instead of onComplete()

    const getMetadata = () => ({
        first_name: formData.firstName,
        last_name: formData.lastName,
        age: formData.age,
        preferred_countries: formData.preferredCountries,
        preferred_sectors: formData.preferredSectors,
        newsletter_optin: formData.newsletter
    });

    // Apple Auth Removed per request

    // Google Auth Removed per request

    const handleEmailSignUp = async () => {
        if (!formData.tos) return;
        setEmailError('');
        if (!password) {
            setEmailError('Veuillez créer un mot de passe.');
            return;
        }
        if (password.length < 6) {
            setEmailError('Le mot de passe doit faire au moins 6 caractères.');
            return;
        }

        setIsSubmitting(true);
        try {
            const { data, error } = await supabase.auth.signUp({
                email: formData.email,
                password: password,
                options: {
                    data: getMetadata(),
                    emailRedirectTo: window.location.origin + '/onboarding' // Redirect to THIS flow
                }
            });

            if (error) throw error;

            if (data?.user) {
                // Check if user is already confirmed (if they exist, identity might be returned)
                // If Supabase "Prevent user enumeration" is OFF, we might get an error above.
                // If ON, we get a fake success.

                if (data.user.identities?.length === 0) {
                    // In some configs, existing user returns empty identities array
                    setEmailError('Cet email est déjà rattaché à un compte.');
                    setIsSubmitting(false);
                    return;
                }

                identifyUser(data.user.id);
                track('signup_completed', { method: 'email' });

                if (!data.session) {
                    // User created but email not confirmed (Session is null)
                    setIsSubmitting(false);
                    // Instead of Alert, show UI
                    setIsWaitingForVerification(true);
                    return;
                }

                // SUCCESS (Session exists): Go to CV step
                setIsSubmitting(false);
                setStep(8);
            }
        } catch (error) {
            console.error('Email Auth Error:', error);
            if (error.message.includes('already registered') || error.message.includes('unique constraint')) {
                setEmailError('Cet email est déjà rattaché à un compte.');
            } else if (error.message.toLowerCase().includes('rate limit')) {
                setEmailError("Trop de tentatives. Veuillez attendre un peu.");
            } else if (error.message.toLowerCase().includes('error sending')) {
                setEmailError("Problème d'envoi d'email (Quota ou Configuration). Réessayez plus tard.");
            } else {
                setEmailError(error.message);
            }
            setIsSubmitting(false);
        }
    };


    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <TightLayout
                        title="Comment vous appelez-vous ?"
                        illustration={mascotsImg}
                        animateIllustration={false}
                        largeIllustration={true}
                    >
                        <div className="flex flex-col gap-4 w-full max-w-sm">
                            <input
                                placeholder="Prénom"
                                value={formData.firstName}
                                onChange={(e) => setFormData({ ...formData, firstName: cleanName(e.target.value) })}
                                onKeyDown={handleKeyDown}
                                className="w-full bg-white border-2 border-slate-100 rounded-2xl px-6 py-5 text-lg font-medium text-slate-700 shadow-sm focus:ring-4 focus:ring-blue-600/10 focus:border-blue-600 transition-all outline-none"
                            />
                            <input
                                placeholder="Nom"
                                value={formData.lastName}
                                onChange={(e) => setFormData({ ...formData, lastName: cleanName(e.target.value) })}
                                onKeyDown={handleKeyDown}
                                className="w-full bg-white border-2 border-slate-100 rounded-2xl px-6 py-5 text-lg font-medium text-slate-700 shadow-sm focus:ring-4 focus:ring-blue-600/10 focus:border-blue-600 transition-all outline-none"
                            />
                        </div>
                    </TightLayout>
                );
            case 2:
                return (
                    <TightLayout title="Choisissez votre avatar">
                        <div className="relative w-full h-[350px] flex items-center justify-center overflow-hidden">
                            {/* Circular Platform Effect */}
                            <div className="absolute bottom-8 w-80 h-28 bg-slate-200/50 rounded-[100%] blur-2xl" />

                            <div className="relative flex items-center justify-center w-full">
                                <button
                                    onClick={prevAvatar}
                                    className="absolute left-0 z-20 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all active:scale-90"
                                >
                                    <ChevronLeft size={24} />
                                </button>

                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={avatarIndex}
                                        initial={{
                                            opacity: 0,
                                            scale: 0.8,
                                            x: direction > 0 ? 100 : -100
                                        }}
                                        animate={{
                                            opacity: 1,
                                            scale: 1,
                                            x: 0
                                        }}
                                        exit={{
                                            opacity: 0,
                                            scale: 0.8,
                                            x: direction > 0 ? -100 : 100
                                        }}
                                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                                        className="relative w-[280px] h-[280px]"
                                    >
                                        <img
                                            src={MASCOTS[avatarIndex]}
                                            alt="Mascot Selection"
                                            className="w-full h-full object-contain filter drop-shadow-[0_25px_25px_rgba(0,0,0,0.15)]"
                                        />
                                    </motion.div>
                                </AnimatePresence>

                                <button
                                    onClick={nextAvatar}
                                    className="absolute right-0 z-20 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-400 hover:text-blue-600 transition-all active:scale-90"
                                >
                                    <ChevronLeft size={24} className="rotate-180" />
                                </button>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowAvatarPanel(true)}
                            className="-mt-4 text-xs font-bold text-blue-600 uppercase tracking-widest bg-blue-50 px-4 py-1.5 rounded-full border border-blue-100 hover:bg-blue-100 transition-colors cursor-pointer"
                        >
                            Voir tous les avatars
                        </button>
                    </TightLayout >
                );
            case 3:
                return (
                    <TightLayout title="Quel est votre âge ?">
                        <div className="flex flex-col gap-4 w-full max-w-xs mt-8">
                            <div className="relative">
                                <input
                                    type="number"
                                    min="18"
                                    max="99"
                                    placeholder="Votre âge (ex: 24)"
                                    value={formData.age || ''}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value);
                                        setFormData({ ...formData, age: isNaN(val) ? '' : val });
                                    }}
                                    onKeyDown={handleKeyDown}
                                    className="w-full bg-white border-2 border-slate-100 rounded-2xl px-6 py-5 text-center text-lg font-black text-slate-800 shadow-sm focus:ring-4 focus:ring-blue-600/10 focus:border-blue-600 transition-all outline-none appearance-none"
                                />
                                <span className="absolute right-8 top-1/2 -translate-y-1/2 text-slate-400 font-bold">ans</span>
                            </div>

                            {/* Age Validation Messages */}
                            {formData.age && (
                                <div className="text-center px-4">
                                    {formData.age >= 29 ? (
                                        <p className="text-sm text-red-500 font-bold animate-pulse">
                                            Le dispositif VIE n'est accessible qu'au 18-28 ans.
                                        </p>
                                    ) : formData.age === 28 ? (
                                        <p className="text-xs text-orange-500 font-bold">
                                            Le départ en V.I.E. doit se faire au plus tard le 1er du mois de passage à 29 ans.
                                        </p>
                                    ) : (
                                        /* 18-27: Show nothing per request "si age >= 27, rien dire" (interpreted as standard valid range) */
                                        null
                                    )}
                                </div>
                            )}

                            {!formData.age && (
                                <p className="text-center text-sm text-slate-400 font-medium px-4">
                                    L'âge est un critère important pour l'éligibilité au VIE (18-28 ans).
                                </p>
                            )}
                        </div>
                    </TightLayout>
                );
            case 4:
                // Country preference selection
                const toggleCountry = (code) => {
                    setFormData(prev => ({
                        ...prev,
                        preferredCountries: prev.preferredCountries.includes(code)
                            ? prev.preferredCountries.filter(c => c !== code)
                            : [...prev.preferredCountries, code]
                    }));
                };

                const filteredCountries = availableCountries.filter(c =>
                    c.name.toLowerCase().includes(countrySearch.toLowerCase()) && c.code !== 'OTHER'
                );

                return (
                    <TightLayout title="Sélectionne tes pays préférés">
                        <div className="w-full max-w-md flex flex-col gap-4">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type="text"
                                    placeholder="Rechercher un pays..."
                                    className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400"
                                    value={countrySearch}
                                    onChange={(e) => setCountrySearch(e.target.value)}
                                />
                            </div>

                            {formData.preferredCountries.length > 0 && (
                                <div className="flex flex-wrap gap-2 animate-in fade-in slide-in-from-top-2">
                                    {formData.preferredCountries.map(code => {
                                        const country = availableCountries.find(c => c.code === code) || { name: code, flag: '🌍' };
                                        return (
                                            <button
                                                key={code}
                                                onClick={() => toggleCountry(code)}
                                                className="flex items-center gap-1 pl-2 pr-1 py-1 bg-violet-100/50 border border-violet-200 text-violet-700 rounded-full text-xs font-bold hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all group"
                                            >
                                                <span>{country.flag} {country.name}</span>
                                                <div className="w-4 h-4 rounded-full bg-violet-200 group-hover:bg-red-200 flex items-center justify-center ml-1">
                                                    <span className="text-[10px]">✕</span>
                                                </div>
                                            </button>
                                        )
                                    })}
                                </div>
                            )}

                            <div className="grid grid-cols-3 gap-3 overflow-y-auto max-h-[420px] pr-1 custom-scrollbar">
                                {isLoadingData ? (
                                    <div className="col-span-3 flex justify-center py-8"><Loader2 className="animate-spin text-blue-500" /></div>
                                ) : (
                                    <>
                                        {filteredCountries.map(country => (
                                            <button
                                                key={country.code}
                                                onClick={() => toggleCountry(country.code)}
                                                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all min-h-[100px] justify-center relative overflow-hidden group ${formData.preferredCountries.includes(country.code)
                                                    ? 'bg-blue-50 border-blue-400 shadow-md transform scale-[0.98]'
                                                    : 'bg-white border-slate-100 hover:border-blue-200 hover:shadow-sm'
                                                    }`}
                                            >
                                                <div className="w-10 h-10 rounded-full overflow-hidden relative shadow-sm border border-slate-50 bg-slate-50 flex-shrink-0">
                                                    {country.localFlag ? (
                                                        <img
                                                            src={country.localFlag}
                                                            alt={country.name}
                                                            className={`w-full h-full object-cover transform ${(country.code === 'DE' ? 'scale-[1.8]' :
                                                                country.code === 'MX' ? 'scale-[1.7]' :
                                                                    country.code === 'CH' ? 'scale-[1.6]' :
                                                                        'scale-[1.4]')
                                                                }`}
                                                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                                        />
                                                    ) : null}
                                                    <img
                                                        src={`https://flagcdn.com/w80/${country.code.toLowerCase()}.png`}
                                                        className="w-full h-full object-cover scale-125 hidden bg-gray-100"
                                                        style={{ display: country.localFlag ? 'none' : 'block' }}
                                                    />
                                                </div>
                                                <span className={`text-xs font-bold text-center leading-tight line-clamp-2 ${formData.preferredCountries.includes(country.code) ? 'text-blue-700' : 'text-slate-600'}`}>
                                                    {country.name}
                                                </span>
                                            </button>
                                        ))}

                                        {!countrySearch && (
                                            <button
                                                onClick={() => toggleCountry('OTHER')}
                                                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all min-h-[100px] justify-center ${formData.preferredCountries.includes('OTHER')
                                                    ? 'bg-blue-50 border-blue-400 shadow-md'
                                                    : 'bg-white border-slate-100 hover:border-blue-200'
                                                    }`}
                                            >
                                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-xl">
                                                    🌍
                                                </div>
                                                <span className="text-xs font-bold text-center text-slate-600">Autre</span>
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </TightLayout>
                );
            case 5:
                // Sector preference selection
                const toggleSector = (name) => {
                    setFormData(prev => ({
                        ...prev,
                        preferredSectors: prev.preferredSectors.includes(name)
                            ? prev.preferredSectors.filter(s => s !== name)
                            : [...prev.preferredSectors, name]
                    }));
                };
                return (
                    <TightLayout title="Sélectionne tes secteurs d'activité">
                        <div className="w-full max-w-sm">
                            <div className="flex flex-col gap-2">
                                {(availableSectors.length > 0 ? availableSectors : SECTORS).map(sector => (
                                    <button
                                        key={sector.id || sector.name}
                                        onClick={() => toggleSector(sector.name)} // Pass name, not ID
                                        className={`flex items-center gap-3 px-4 py-3 rounded-2xl border-2 transition-all ${formData.preferredSectors.includes(sector.name)
                                            ? 'bg-blue-50 border-blue-400 shadow-md'
                                            : 'bg-white border-slate-100 hover:border-slate-300'
                                            }`}
                                    >
                                        <span className="text-xl flex-shrink-0">{sector.icon}</span>
                                        <span className={`text-sm font-bold truncate ${formData.preferredSectors.includes(sector.name) ? 'text-blue-600' : 'text-slate-700'}`}>
                                            {sector.name}
                                        </span>
                                        {formData.preferredSectors.includes(sector.name) && (
                                            <Check className="ml-auto text-blue-600" size={20} />
                                        )}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-4 text-center text-sm text-slate-400 font-medium">
                                {formData.preferredSectors.length} secteur{formData.preferredSectors.length > 1 ? 's' : ''} sélectionné{formData.preferredSectors.length > 1 ? 's' : ''}
                            </p>
                        </div>
                    </TightLayout>
                );
            case 6:
                // --- CONSOLIDATED AUTH SCREEN ---
                if (isWaitingForVerification) {
                    const handleCheckVerification = async () => {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (session?.user) {
                            setStep(7);
                        } else {
                            // Force refresh session au cas où le token est en cache
                            await supabase.auth.refreshSession();
                            const { data: { session: refreshed } } = await supabase.auth.getSession();
                            if (refreshed?.user) {
                                setStep(7);
                            } else {
                                alert("Votre email n'est pas encore confirmé. Vérifiez votre boîte mail (et vos spams !).");
                            }
                        }
                    };

                    return (
                        <div className="w-full max-w-xl mx-auto flex flex-col items-center h-[520px] justify-center animate-in fade-in zoom-in duration-300">
                            <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                                <Mail size={48} className="text-blue-600 animate-pulse" />
                            </div>
                            <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight text-center leading-tight">
                                Vérifiez vos emails ! 📧
                            </h2>
                            <p className="text-slate-600 text-center text-lg max-w-sm mb-6 leading-relaxed">
                                Un lien de confirmation a été envoyé à <strong>{formData.email}</strong>.
                            </p>
                            <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl text-center max-w-sm mb-6">
                                <p className="text-sm text-amber-800 font-bold">
                                    📂 Pensez à vérifier vos spams si vous ne voyez pas l'email !
                                </p>
                            </div>
                            <button
                                onClick={handleCheckVerification}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-2xl shadow-xl shadow-blue-600/20 transition-all active:scale-95"
                            >
                                J'ai validé mon compte ✅
                            </button>
                        </div>
                    );
                }

                return (
                    <TightLayout
                        title="Créez votre compte"
                        illustration={paperPlaneImg}
                        largeIllustration={false}
                    >
                        <div className="w-full max-w-sm flex flex-col gap-2">
                            {/* TOS Checkbox - at the top */}
                            <label className="flex items-start gap-3 cursor-pointer group pb-2">
                                <div className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-all ${formData.tos ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-600/30' : 'bg-white border-slate-200 group-hover:border-blue-600'}`}>
                                    <input
                                        type="checkbox"
                                        className="hidden"
                                        checked={formData.tos}
                                        onChange={() => setFormData({ ...formData, tos: !formData.tos })}
                                    />
                                    {formData.tos && <Check size={12} strokeWidth={4} />}
                                </div>
                                <span className="text-xs text-slate-600 font-medium leading-snug">
                                    J'accepte les <a href="/cgu" target="_blank" className="text-blue-600 hover:underline">Conditions Générales</a>
                                </span>
                            </label>

                            {/* Email Input - Moved up since Social Auth is removed */}

                            {/* Email Input */}
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => {
                                    setFormData({ ...formData, email: e.target.value });
                                    if (emailError) setEmailError('');
                                }}
                                placeholder="votre@email.com"
                                onKeyDown={handleKeyDown}
                                className={`w-full bg-white border-2 rounded-2xl px-5 py-3 text-base font-bold text-slate-800 shadow-sm focus:ring-4 transition-all outline-none ${emailError
                                    ? 'border-red-300 focus:ring-red-100 focus:border-red-400'
                                    : 'border-slate-100 focus:ring-blue-600/10 focus:border-blue-600'
                                    }`}
                            />

                            {/* Password Input - always visible */}
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Mot de passe (6 caractères min)"
                                    className="w-full bg-white border-2 border-slate-100 rounded-2xl px-5 py-3 text-base font-medium text-slate-800 shadow-sm focus:ring-4 focus:ring-blue-600/10 focus:border-blue-600 transition-all outline-none pr-12"
                                />
                                <button
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>

                            {emailError && <p className="text-red-500 text-xs font-bold ml-1">{emailError}</p>}

                            {/* Create Account Button */}
                            <button
                                onClick={handleEmailSignUp}
                                disabled={!formData.tos || isSubmitting}
                                className="w-full py-3 rounded-2xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : 'Créer mon compte'}
                            </button>
                        </div>
                    </TightLayout>
                );


            case 7:
                // --- CV UPLOAD SCREEN ---
                return (
                    <TightLayout
                        title={showWelcome ? "Bienvenue sur Projet Vie ! 🎉" : "Un dernier détail ! 📄"}
                        subtitle={showWelcome ? "Votre compte est validé. Ajoutez votre CV pour finaliser votre profil." : "Ajoutez votre CV (PDF) pour qu'on pré-remplisse automatiquement votre profil."}
                    >
                        <div className="w-full max-w-sm flex-1 flex flex-col justify-center">
                            {showWelcome && (
                                <motion.div
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6 flex gap-3 items-start"
                                >
                                    <div className="bg-green-100 p-2 rounded-full">
                                        <Check size={20} className="text-green-600" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-green-800 text-sm">Email vérifié avec succès</h4>
                                        <p className="text-xs text-green-700 mt-1">
                                            Vous faites maintenant partie de la communauté. L'upload de votre CV est la dernière étape avant d'accéder aux offres.
                                        </p>
                                    </div>
                                </motion.div>
                            )}

                            {isUploading ? (
                                <CircularProgress progress={uploadProgress} />
                            ) : (
                                <>
                                    <label className={`
                                        w-full h-48 border-4 border-dashed rounded-3xl flex flex-col items-center justify-center cursor-pointer transition-all relative overflow-hidden group mb-6
                                        ${cvFile ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-blue-400 hover:bg-blue-50'}
                                    `}>
                                        <input type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />

                                        {cvFile ? (
                                            <>
                                                <Check size={48} className="text-green-600 mb-2" />
                                                <span className="font-bold text-green-700 text-lg px-4 text-center break-all">{cvFile.name}</span>
                                                <span className="text-xs text-green-600 mt-1">Cliquez pour changer</span>
                                            </>
                                        ) : (
                                            <>
                                                <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                                    <ArrowRight className="rotate-[-90deg] text-slate-400 group-hover:text-blue-500" />
                                                </div>
                                                <span className="font-bold text-slate-600 group-hover:text-blue-600 transition-colors">Cliquez pour déposer</span>
                                                <span className="text-xs text-slate-400 mt-1">Format PDF uniquement</span>
                                            </>
                                        )}
                                    </label>

                                    {/* Info Mistral AI - FLASHY VERSION */}
                                    <div className="flex items-center justify-center gap-2 mb-6">
                                        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg shadow-indigo-500/30 flex items-center gap-2 animate-pulse">
                                            <Sparkles size={12} className="text-yellow-300" />
                                            <span>Analysé par Mistral AI</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleCvUpload}
                                        disabled={isUploading}
                                        className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-xl
                                            ${cvFile
                                                ? 'bg-slate-900 text-white hover:bg-black hover:scale-[1.02] shadow-slate-900/20'
                                                : 'bg-white border-2 border-slate-200 text-slate-400 hover:bg-slate-50'}
                                        `}
                                    >
                                        {cvFile ? 'Mettre à jour mon profil' : 'Passer cette étape'}
                                    </button>
                                </>
                            )}
                        </div>
                    </TightLayout>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-[#F0F9FF] flex flex-col items-center justify-between p-6">
            <Helmet><meta name='robots' content='noindex, nofollow' /></Helmet>
            {/* Header */}
            <div className="w-full max-w-4xl flex items-center justify-between py-4">
                {step > 1 ? (
                    <button
                        onClick={prevStep}
                        className="w-12 h-12 flex items-center justify-center bg-slate-50 hover:bg-slate-100 rounded-full text-slate-600 transition-all shadow-sm"
                    >
                        <ChevronLeft size={24} />
                    </button>
                ) : <div className="w-12 h-12" />}

                {/* --- IMPROVED PROGRESS BAR --- */}
                <ProgressBar current={step > 2 ? step - 1 : step} total={6} />

                <div className="w-12 h-12" />
            </div>

            {/* Content Area */}
            <div className="flex-1 w-full flex items-start pt-2 justify-center overflow-hidden">
                <AnimatePresence initial={false} custom={direction} mode='wait'>
                    <motion.div
                        key={step}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            x: { type: "spring", stiffness: 300, damping: 30 },
                            opacity: { duration: 0.2 }
                        }}
                        className="w-full h-full flex items-center justify-center"
                    >
                        {renderStep()}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Footer Navigation (Steps 1-5 only, Step 6 has its own auth buttons) */}
            {step < 6 && (
                <div className="w-full max-w-4xl flex flex-col items-center pb-4 gap-0">
                    <button
                        onClick={nextStep}
                        disabled={
                            (step === 1 && (!formData.firstName.trim() || !formData.lastName.trim())) ||
                            (step === 3 && (!formData.age || formData.age < 18)) ||
                            (step === 4 && formData.preferredCountries.length === 0) ||
                            (step === 5 && formData.preferredSectors.length === 0)
                        }
                        className={`w-full max-w-[320px] py-4 rounded-[28px] font-bold text-lg flex items-center justify-center gap-3 transition-all transform active:scale-95 shadow-xl ${'bg-blue-600 text-white shadow-blue-600/20'
                            } disabled:opacity-30 disabled:grayscale cursor-pointer disabled:cursor-not-allowed`}
                    >
                        Continuer
                        <ArrowRight size={20} strokeWidth={3} />
                    </button>
                </div>
            )}

            {/* Avatar Panel Overlay components would go here if needed, code structure preserved */}
            <AnimatePresence>
                {showAvatarPanel && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-sm"
                        onClick={() => setShowAvatarPanel(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="bg-white rounded-[32px] shadow-2xl w-full max-w-2xl p-8 max-h-[80vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-8">
                                <h3 className="text-2xl font-black text-slate-900 tracking-tight">Tous les avatars</h3>
                                <button
                                    onClick={() => setShowAvatarPanel(false)}
                                    className="w-10 h-10 flex items-center justify-center bg-slate-50 hover:bg-slate-100 rounded-full text-slate-400 transition-all"
                                >
                                    <ChevronDown size={24} />
                                </button>
                            </div>

                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 mb-10">
                                {MASCOTS.map((mascot, index) => (
                                    <div
                                        key={index}
                                        onClick={() => {
                                            setAvatarIndex(index);
                                        }}
                                        className={`
                                            aspect-square rounded-2xl border-2 transition-all cursor-pointer p-2 flex items-center justify-center relative group overflow-hidden
                                            ${avatarIndex === index
                                                ? 'border-blue-600 bg-blue-50 shadow-md ring-4 ring-blue-600/10'
                                                : 'border-slate-100 bg-white hover:border-blue-200'}
                                        `}
                                    >
                                        <img
                                            src={mascot}
                                            alt={`Avatar ${index + 1}`}
                                            className="w-full h-full object-contain group-hover:scale-110 transition-transform"
                                            style={{ transform: `scale(${getAvatarScale(index)})` }}
                                        />
                                        {avatarIndex === index && (
                                            <div className="absolute top-2 right-2 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center border-2 border-white shadow-sm z-10">
                                                <Check size={14} strokeWidth={4} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-center pt-2">
                                <button
                                    onClick={() => setShowAvatarPanel(false)}
                                    className="bg-blue-600 text-white px-10 py-4 rounded-3xl font-black text-lg shadow-xl shadow-blue-600/20 hover:bg-blue-700 transition-all active:scale-95 cursor-pointer"
                                >
                                    Appliquer la sélection
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};


export default OnboardingFlow;
